import { HTTPError } from 'ky';
import { http } from '@/lib/http';
import { newIdempotencyKey } from '@/lib/uuid';
import type { IRecoveryBulkResult, IRecoveryView } from './types';

// `operator-bff:purchase-recovery@v1` (§6.6). Every write carries an Idempotency-Key
// minted once per operator intent; `buy` and `give-up-unconfirmed` are supervisor-only
// at the BFF (the console gates them too).

const codes = new WeakMap<Response, string>();
const safe = (v: unknown): v is string => typeof v === 'string' && /^[A-Za-z][A-Za-z0-9_]{0,80}$/.test(v);
// Capture the domain's safe code before the shared client's error hook consumes the body. Built
// on first use: screens whose tests stub `http` import this module too.
let extended: typeof http | undefined;
const client = () =>
  (extended ??= http.extend({
    hooks: {
      afterResponse: [
        async (_req, _opts, response) => {
          if (!response.ok) {
            try {
              const body = (await response.clone().json()) as { code?: unknown };
              if (safe(body.code)) codes.set(response, body.code);
            } catch {
              /* No body: the status speaks. */
            }
          }
          return response;
        },
      ],
    },
  }));

const group = (groupId: string) => `v1/groups/${encodeURIComponent(groupId)}/recovery`;
const bulk = (disruptionUrn: string) => `v1/disruptions/${encodeURIComponent(disruptionUrn)}/recovery`;

function post<T>(path: string, json: Record<string, unknown>, key: string): Promise<T> {
  return client().post(path, { json, headers: { 'Idempotency-Key': key } }).json<T>();
}

export const recoveryApi = {
  view: (caseUrn: string, groupId: string) =>
    client().get(group(groupId), { searchParams: { caseUrn } }).json<IRecoveryView>(),
  search: (caseUrn: string, groupId: string, key = newIdempotencyKey()) =>
    post<IRecoveryView>(`${group(groupId)}/search`, { caseUrn }, key),
  offer: (caseUrn: string, groupId: string, optionUrn: string, key = newIdempotencyKey()) =>
    post<IRecoveryView>(`${group(groupId)}/offer`, { caseUrn, optionUrn }, key),
  buy: (caseUrn: string, groupId: string, optionUrn: string, key = newIdempotencyKey()) =>
    post<IRecoveryView>(`${group(groupId)}/buy`, { caseUrn, optionUrn }, key),
  reconcile: (caseUrn: string, groupId: string, key = newIdempotencyKey()) =>
    post<IRecoveryView>(`${group(groupId)}/reconcile`, { caseUrn }, key),
  giveUpUnconfirmed: (caseUrn: string, groupId: string, key = newIdempotencyKey()) =>
    post<IRecoveryView>(`${group(groupId)}/give-up-unconfirmed`, { caseUrn, attested: true }, key),
  noFlight: (caseUrn: string, groupId: string, key = newIdempotencyKey()) =>
    post<IRecoveryView>(`${group(groupId)}/no-flight`, { caseUrn }, key),
  bulkSearch: (disruptionUrn: string, subCaseUrns: string[], key = newIdempotencyKey()) =>
    post<IRecoveryBulkResult>(`${bulk(disruptionUrn)}/search`, { subCaseUrns }, key),
  bulkOfferBest: (disruptionUrn: string, subCaseUrns: string[], key = newIdempotencyKey()) =>
    post<IRecoveryBulkResult>(`${bulk(disruptionUrn)}/offer-best`, { subCaseUrns }, key),
};

export const recoveryKeys = {
  all: ['recovery'] as const,
  view: (caseUrn: string, groupId: string) => ['recovery', caseUrn, groupId] as const,
};

/** The safe error codes the console has copy for (`pnr:recovery.error.*`). */
export type RecoveryErrorCode =
  | 'FORBIDDEN'
  | 'UNCONFIRMED'
  | 'MOVED'
  | 'NO_CAPACITY'
  | 'TOO_SOON'
  | 'CONFLICT'
  | 'UNAVAILABLE';

/** A failed recovery write as the console words it — by status and the domain's code, never
 *  upstream text: 409 `PURCHASE_UNCONFIRMED` (a purchase is being verified), `NOT_A_CANDIDATE` /
 *  `RECOVERY_MOVED` (the options changed under the click), any other 409 / 423 (busy or moved on). */
export function recoveryErrorCode(error: unknown): RecoveryErrorCode {
  if (error instanceof HTTPError) {
    const status = error.response.status;
    if (status === 401 || status === 403) return 'FORBIDDEN';
    if (status === 409 || status === 423) {
      const code = codes.get(error.response);
      if (code === 'PURCHASE_UNCONFIRMED') return 'UNCONFIRMED';
      if (code === 'NOT_A_CANDIDATE' || code === 'RECOVERY_MOVED') return 'MOVED';
      // The flight has no room left for this group, or leaves too soon to make it (offer / buy).
      if (code === 'NO_CAPACITY' || code === 'TOO_SOON') return code;
      return 'CONFLICT';
    }
  }
  return 'UNAVAILABLE';
}
